import React, { useState } from 'react';
import { Field, Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ComboBox, Button, TextInput, Toggler, Radio } from '@components';
import { PPISchema, PPIInitialValues, PPIDocs } from '@schemas';
import { enviarRequerimentoFormulario } from '../../../../store/requerimentos/thunks';

import { ModalUpload } from './components/ModalUpload';
import { validaUpload } from './utils/validaUpload';
import { DadosUsuario } from './components/DadosUsuario';

const Anexos = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {PPIDocs.commons.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

export const PPI = ({ title, reference }) => {
  const dispatch = useDispatch();
  const envioLoading = useSelector(
    (state) => state.requerimentos.envio.loading
  );
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="card">
      <div className="card-body">
        {showForm ? (
          <Formik
            validateOnMount
            initialValues={PPIInitialValues}
            validationSchema={PPISchema}
            onSubmit={(values) => {
              dispatch(
                enviarRequerimentoFormulario({
                  campos: values,
                  formulario: reference,
                })
              )
                .unwrap()
                .then(() => {
                  navigate('/requerimentos/andamento');
                });
            }}
          >
            {({ handleSubmit, isValid }) => (
              <Form>
                <h3 className="fw-bold ts-20 mb-32">
                  Opção de Regime de Tributação
                </h3>

                <p className="mb-32">
                  Escolha o Regime de Tributação do Imposto de Renda que será
                  calculado no seu requerimento com base na Lei nº 11.053 de
                  2004, alterada pela Lei nº 14.803 de 2024. Essa Lei permite
                  aos participantes de planos de previdência a escolha de seu
                  regime de tributação na ocasião da obtenção do benefício ou do
                  primeiro resgate dos valores acumulados.
                </p>

                <p className="mb-32">
                  <strong>Formalizo a minha opção pelo Regime: </strong>
                </p>

                <div className="row">
                  <Field
                    className="mb-5 ms-3"
                    name="tipoRegime"
                    component={Radio}
                    value="Progressivo"
                    label="Progressivo: os valores recebidos da Forluz serão tributados conforme tabela para cálculo do Imposto de Renda (IR), o que significa que o desconto do imposto na fonte terá alíquotas variáveis conforme o montante recebido, ou seja, quanto maior o valor, maior a alíquota. Atualmente, as alíquotas variam de 0% a 27,5%. Nesse regime poderão ocorrer deduções mensais e ajustes na declaração anual de Imposto de Renda. "
                  />
                </div>

                <div className="row">
                  <Field
                    className="mb-3 ms-3"
                    name="tipoRegime"
                    component={Radio}
                    value="Regressivo"
                    label="Regressivo: neste caso, o IR pago no recebimento da Forluz vai variar entre 35% e 10% de acordo com o tempo de aporte de recursos ao plano, ou seja, quanto maior o tempo, menor a alíquota. A tributação será exclusivamente na fonte, ou seja, não serão realizados ajustes anuais na declaração de Imposto de Renda. "
                  />
                </div>

                <p className="mb-32 mt-32">
                  <strong>Declarações</strong>
                </p>

                <div className="row">
                  <Field
                    className="mb-3"
                    name="aceite"
                    component={Toggler}
                    label="Declaro ciência de que a opção marcada é IRRETRATÁVEL, ou seja, saques posteriores ou resgates terão a tributação calculada de acordo com essa escolha."
                  />
                </div>

                <div className="row">
                  <Field
                    className="mb-3"
                    name="declaracaoTipoRegime"
                    component={Toggler}
                    label="Declaro ciência de que caso já tenha feito algum resgate, será desconsiderada a opção marcada acima e será aplicado o regime escolhido no primeiro resgate. "
                  />
                </div>

                <hr className="mt-32 mt-xs-0 mb-32" />

                <h3 className="fw-bold ts-20 mb-32">{title}</h3>

                <p className="mb-4">
                  Venho, por meio deste, requerer à Fundação Forluminas de
                  Seguridade Social - Forluz o cancelamento de minha inscrição,
                  com o pagamento da Renda por Invalidez, nos termos do
                  Regulamento do Plano Taesa de Benefícios Previdenciários
                  (Plano Taesaprev) e das normas vigentes e aquelas que venham a
                  alterá-las ou substituí-las. Para tanto, anexo a documentação
                  necessária para a finalidade.
                </p>
                <div className="row">
                  <div className="col-md-12 col-lg-6">
                    <Field
                      className="mb-3"
                      name="acessoContraCheque"
                      label="Acesso ao contracheque via"
                      component={ComboBox}
                      options={['Portal', 'E-mail e portal'].map((x) => {
                        return { value: x, label: x };
                      })}
                    />
                  </div>
                </div>

                <div className="row">
                  <Field
                    className="mb-3"
                    name="declaracao"
                    component={Toggler}
                    label="Declaro estar ciente de que o cancelamento da minha inscrição implica na extinção de quaisquer direitos e deveres referentes ao Plano Taesaprev e à Forluz, à exceção do pagamento do valor do saldo de conta de aposentadoria. Dessa forma, dou à FORLUZ plena, rasa, geral e irrevogável quitação, nada mais tendo a reclamar, seja a que título for, em juízo ou fora dele"
                  />
                </div>

                <div className="row">
                  <div className="col-md-12 ">
                    <p className="mb-4">
                      Com base na Lei nº 13.709/2018 (Lei Geral de Proteção de
                      Dados Pessoais), autorizo o tratamento dos meus dados
                      pessoais e dados pessoais sensíveis para fins relacionados
                      ao meu vínculo com a FORLUZ.
                    </p>
                    <Field
                      className="mb-3"
                      name="consentimentoDadosSensiveis"
                      component={Toggler}
                      label="Consentimento dados sensíveis (obrigatório)"
                    />
                  </div>
                  <Field
                    className="mb-3"
                    name="aceiteInss"
                    component={Toggler}
                    createTermsField
                    label="Estou ciente que pessoa aposentada pela Previdência Oficial (INSS) e acometida por moléstia grave faz jus à isenção de Imposto de Renda. Este pedido poderá ser solicitado à Forluz a qualquer tempo através do formulário Isenção de Imposto de Renda disponível no menu Requerimentos. Se deferida, a isenção será processada no mês seguinte ao requerimento. "
                  />
                </div>

                <Anexos />

                <Field
                  className="mb-3"
                  name="declaracaoVeracidade"
                  component={Toggler}
                  createTermsField
                  label="Declaro que as informações e os documentos por mim apresentados à Forluz são verdadeiros, estando ciente de que, caso não sejam autênticos, estarei sujeito às consequências civis e criminais cabíveis, além de autorizar a Forluz a anular o ato praticado ou o benefício concedido."
                />

                <hr className="mt-32 mt-xs-0 mb-32" />

                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        disabled={!isValid || envioLoading}
                        className="ms-0 ms-md-3"
                        onClick={handleSubmit}
                      >
                        Continuar
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <DadosUsuario
            onClick={() => {
              window.scroll(0, 0);
              setShowForm(true);
            }}
            dadosBancarios
          />
        )}
      </div>
    </div>
  );
};
